import React, { useEffect, useState } from "react";
import Modal from "components/ui/Modal";
import { getIcon } from "utils/iconsConfig";
import apiClient from "lib/api-client";

const BillingSummaryModal = ({ open, onClose, data, transactionNumber, type }) => {
  const [billingData, setBillingData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentRate, setCurrentRate] = useState("");
  // 🔹 Safe default structure (so future API integration is smooth)
  const billingData1 = data || {
    invoiceNumber: "DAE | 24 – 25 | 8576", //
    clientName: "Reshu Singh",
    time: "12:29:41 PM",
    sections: {
      material: [
        { label: "Gold", value: "6.45g / 7.11g", amount: "₹ 2,19,500" },
        { label: "Diamond", value: "4pcs / 0.17ct", amount: "₹ 2,59,000" }, //
        { label: "Color Stone", value: "2pcs / 4.55ct", amount: "₹ 45,000" }, //
        { label: "Other Material", value: "55pcs / 4.604g", amount: "₹ 4,000" },
      ],
      grossTax: [
        { label: "Gross Amount", amount: "₹ 6,35,300" },
        { label: "Shipping & Tax", amount: "₹ 21,500" },
      ],
      labour: [
        { label: "Wastage Charges", value: "0", amount: "₹ 13,500" },
        { label: "Extra Charges", value: "1.15g", amount: "₹ 89,300" },
      ],
    },
    totals: {
      material: "₹ 5,32,500",
      grossTax: "₹ 6,13,500",
      labour: "₹ 1,02,800",
      netAmount: "₹ 6,13,500",
    },
  };

  const formatCurrency = (num) => (num ? `₹ ${num.toLocaleString("en-IN")}` : "₹ 0");

  const formatTime = (isoDate) =>
    new Date(isoDate).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

  const calculateTotal = (amounts) => {
    let total = amounts.reduce((sum, value) => sum + value, 0);
    return formatCurrency(total);
  };

  const toNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const transformInfoData = (backendData, transactionNumber, chargesLabel) => {
    const wastagePercent = toNumber(
      backendData?.WastagePer ??
      backendData?.LossPer ??
      backendData?.WastagePercent ??
      0
    );
    const wastageAmount = toNumber(
      backendData?.WastageAmt ??
      backendData?.LossAmt ??
      backendData?.LabourAmt ??
      0
    );
    const extraChargeAmount = toNumber(backendData?.XchgAmt);

    const formattedInvoice = {
      invoiceNumber: transactionNumber.replaceAll("/", " | "),
      clientName: backendData.CreateBy,
      time: formatTime(backendData.CreateDate),

      sections: {
        material: [
          {
            label: "Gold",
            value: `${backendData.MtlPc || 0}pcs / ${backendData.MtlWt || 0}g`,
            amount: formatCurrency(backendData.MatelAmt),
          },
          {
            label: "Diamond",
            value: `${backendData.DiaPc || 0}pcs / ${backendData.DiaWt || 0}ct`,
            amount: formatCurrency(backendData.DiamondAmt),
          },
          {
            label: "Color Stone",
            value: `${backendData.CSPc || 0}pcs / ${backendData.CSWt || 0}ct`,
            amount: formatCurrency(backendData.StoneAmt),
          },
          {
            label: "Other Material",
            value: `${backendData.OtherMetalPc || 0}pcs / ${backendData.OtherMetalWt || 0}g`,
            amount: formatCurrency(backendData.OtherMetalAmt),
          },
        ],

        grossTax: [
          {
            label: "Gross Amount",
            amount: formatCurrency(backendData.GrossAmount),
          },
          {
            label: "Shipping & Tax",
            amount: formatCurrency(backendData.ShippingTax),
          },
        ],

        labour: [
          {
            label: chargesLabel,
            value: `${wastagePercent}%`,
            amount: formatCurrency(wastageAmount),
          },
          {
            label: "Extra Charges",
            value: `${backendData.XChgWt || 0}g`,
            amount: formatCurrency(extraChargeAmount),
          },
        ],
      },

      totals: {
        material: calculateTotal([backendData.MatelAmt, backendData.DiamondAmt, backendData.StoneAmt, backendData.OtherMetalAmt]),
        grossTax: calculateTotal([backendData.GrossAmount, backendData.ShippingTax]),
        labour: calculateTotal([wastageAmount, extraChargeAmount]),
        netAmount: calculateTotal([
          backendData.MatelAmt,
          backendData.DiamondAmt,
          backendData.StoneAmt,
          backendData.OtherMetalAmt,
          backendData.GrossAmount,
          backendData.ShippingTax,
          wastageAmount,
          extraChargeAmount,
        ]),
      },
    };

    return formattedInvoice;
  };

  const fetchDesignInfoData = async () => {
    try {
      console.log("BillingSummaryModal -> fetching rate master", {
        transactionNumber,
        type,
      });
      const rateMasterResponse = await apiClient.get(`/api/v1/stocks/getUserRateMaster?type=labour`);
      const resolvedCurrentRate =
        rateMasterResponse?.data?.currentLabourChart ||
        "";
      console.log("BillingSummaryModal -> current labour/rate chart:", resolvedCurrentRate);
      setCurrentRate(resolvedCurrentRate);
      const normalizedLabourChart = String(resolvedCurrentRate || "").trim().toUpperCase();
      const useWastageChargesLabel = normalizedLabourChart === "" || normalizedLabourChart.includes("W");
      const chargesLabel = useWastageChargesLabel ? "Wastage Charges" : "Making Charges";

      let infoData = await apiClient(`/api/v9/myBinnys/getDueBillingSummary?transactionNo=${transactionNumber}`);
      if (infoData) {
        setIsLoading(false);
        setBillingData(transformInfoData(infoData?.data?.data, transactionNumber, chargesLabel));
      }
    } catch (error) {
      return false;
    }
  };

  useEffect(() => {
    if (open && transactionNumber !== "Select" && (type === "dues" || type === "ledger")) {
      setIsLoading(true);
      setBillingData(null);
      fetchDesignInfoData();
    }
  }, [open, transactionNumber, type]);

  // useEffect(() => {
  //   if (open && transactionNumber !== "Select") {
  //     fetchDesignInfoData();
  //   }
  // }, [open, isLoading]);

  return (
    <Modal
      open={open}
      handleClose={onClose}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(3px)",
        width: "430px",
      }}
    >
      {isLoading ? (
        <p style={{ color: "#000" }}> Loding ...</p>
      ) : (
        <>
          {/* ---------- Header ---------- */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span
              style={{
                fontWeight: 500,
                fontSize: "16px",
                color: "#000000",
                lineHeight: "100%",
                fontFamily: "Poppins",
              }}
            >
              {billingData.invoiceNumber}
            </span>
            <span
              style={{
                fontSize: "14px",
                color: "#444",
                fontWeight: 500,
                lineHeight: "100%",
              }}
            >
              (Billing Summary)
            </span>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "16px",
            }}
          >
            <span style={infoText}>
              {getIcon("Black_User_Icon")} {billingData.clientName}
            </span>
            <span style={infoText}>{getIcon("Wallet_Icon", "black")} Amount</span>
            <span style={infoText}>
              {getIcon("Black_Clock_Icon")} {billingData.time}
            </span>
          </div>

          <hr style={{ margin: "16px 0", borderColor: "#ddd" }} />

          {/* ---------- Material Section ---------- */}
          <Section title="Material">
            <Table rows={billingData?.sections?.material} total={billingData?.totals?.material} />
          </Section>

          {/* ---------- Gross & Tax Section ---------- */}
          <Section title="Gross & Tax">
            <GrossTaxTable rows={billingData?.sections?.grossTax} total={billingData?.totals?.grossTax} />
          </Section>

          {/* ---------- Labour Section ---------- */}
          <Section title="Labour">
            <Table rows={billingData?.sections?.labour} total={billingData?.totals?.labour} />
          </Section>

          <hr style={{ margin: "20px 0", borderColor: "#ddd" }} />

          {/* ---------- Net Amount ---------- */}
          <div style={{ textAlign: "right", borderRadius: "10px", background: "#EDEDEB", padding: "10px 12px", display: "flex", justifyContent: "space-between" }}>
            <span
              style={{
                fontWeight: 400,
                fontSize: "15px",
                color: "#000",
                fontFamily: "Karla",
                lineHeight: "20px",
              }}
            >
              Net Amount :
            </span>
            <span
              style={{
                fontWeight: 700,
                fontSize: "16px",
                color: "#000",
                fontFamily: "Karla",
                lineHeight: "22px",
                letterSpacing: "-0.41px",
              }}
            >
              {billingData?.totals?.netAmount}
            </span>
          </div>
        </>
      )}
    </Modal>
  );
};

// 🔹 Simple reusable section title
const Section = ({ title, children }) => (
  <div style={{ marginBottom: "18px" }}>
    <div
      style={{
        fontWeight: 400,
        fontSize: "14px",
        color: "#444444",
        marginBottom: "6px",
        fontFamily: "Poppins",
        lineHeight: "22px",
        letterSpacing: "-0.41px",
      }}
    >
      {title}
    </div>
    {children}
  </div>
);

// 🔹 Reusable Table layout
const Table = ({ rows, total }) => (
  <div
    style={{
      //   border: "1px solid #e0e0e0",
      borderRadius: "10px",
      background: "#EDEDEB",
      padding: "10px 12px",
    }}
  >
    <div
      style={{
        display: "flex",
        fontWeight: 300,
        fontSize: "14px",
        fontFamily: "Karla",
        marginBottom: "6px",
        lineHeight: "22px",
        letterSpacing: "-0.41px",
      }}
    >
      <div style={{ width: "40%" }}>Items</div>
      <div style={{ width: "40%" }}>Pieces / Weight</div>
      <div style={{ width: "20%" }}>Amount</div>
    </div>

    {rows?.map((row, i) => (
      <div
        key={i}
        style={{
          display: "flex",
          fontSize: "15px",
          color: "#000",
          marginBottom: "4px",
          fontWeight: 400,
          lineHeight: "20px",
          fontFamily: "Karla",
        }}
      >
        <div style={{ width: "40%" }}>{row.label}</div>
        <div style={{ width: "40%" }}>{row.value || "-"}</div>
        <div style={{ width: "23%" }}>{row.amount}</div>
      </div>
    ))}

    <hr style={{ margin: "8px 0", borderColor: "0.5px solid #8B8B8B" }} />

    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        fontWeight: 400,
        fontSize: "15px",
        fontFamily: "karla",
        lineHeight: "20px",
      }}
    >
      <div>Total</div>
      <div style={{ width: "23%", textAlign: "center" }}>{total}</div>
    </div>
  </div>
);

const GrossTaxTable = ({ rows, total }) => (
  <div
    style={{
      borderRadius: "10px",
      background: "#EDEDEB",
      padding: "10px 12px",
    }}
  >
    <div
      style={{
        display: "flex",
        fontWeight: 300,
        fontSize: "14px",
        fontFamily: "Karla",
        marginBottom: "6px",
        lineHeight: "22px",
        letterSpacing: "-0.41px",
      }}
    >
      <div style={{ width: "80%" }}>Items</div>
      <div style={{ width: "20%" }}>Amount</div>
    </div>

    {rows?.map((row, i) => (
      <div
        key={i}
        style={{
          display: "flex",
          fontSize: "15px",
          color: "#000",
          marginBottom: "4px",
          fontWeight: 400,
          lineHeight: "20px",
          fontFamily: "Karla",
        }}
      >
        <div style={{ width: "80%" }}>{row.label}</div>
        <div style={{ width: "23%" }}>{row.amount}</div>
      </div>
    ))}

    <hr style={{ margin: "8px 0", borderColor: "0.5px solid #8B8B8B" }} />

    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        fontWeight: 400,
        fontSize: "15px",
        fontFamily: "karla",
        lineHeight: "20px",
      }}
    >
      <div>Total</div>
      <div style={{ width: "23%", textAlign: "center" }}>{total}</div>
    </div>
  </div>
);

// Common text style for header info
const infoText = {
  fontSize: "14px",
  fontWeight: 500,
  color: "#000",
  letterSpacing: "0.2px",
  display: "flex",
  alignItems: "center",
  gap: "5px",
  lineHeight: "22px",
  fontFamily: "Poppins",
};

export default BillingSummaryModal;
